import React, { ReactElement, ReactNode } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import cn from 'clsx';
import styles from './styles.module.scss';
import { DropDownList, DropDownListProps, ExcludeArray } from '@components-clean/core/drop-down-list';

interface GroupedDropDownListProps<T> extends DropDownListProps<T> {
    groupField: string;
}

const headerItemRender = (li: ReactElement<HTMLLIElement>) => {
    const itemHeader = <span className={styles.headerItem}>{li.props.children as ReactNode}</span>;
    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/no-clone-element
    return React.cloneElement(li, li.props, itemHeader);
};

const stickyHeaderRender = (div: ReactElement<HTMLDivElement>) => {
    const item = <div className={cn(styles.headerItem, styles.sticky)}>{div.props.children as ReactNode}</div>;
    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/no-clone-element
    return React.cloneElement(div, div.props, item);
};

const GroupedDropdownList = <T,>(props: GroupedDropDownListProps<T>) => (
    <DropDownList
        groupHeaderItemRender={headerItemRender}
        groupStickyHeaderItemRender={stickyHeaderRender}
        {...props}
    />
);

export interface GroupedDropdownListControllerProps<
    TData,
    TControl extends FieldValues,
> extends GroupedDropDownListProps<TData> {
    control: Control<TControl>;
    name: FieldPath<TControl>;
}

const GroupedDropdownListController = <TData, TControl extends FieldValues>({
    control,
    name,
    'data-testid': dataTestId,
    disabled = false,
    label,
    textField,
    dataItemKey,
    required = false,
    data,
    groupField,
    ...props
}: GroupedDropdownListControllerProps<TData, TControl>) => {
    const { t } = useTranslation();
    return (
        <Controller
            control={control}
            name={name}
            rules={{
                required: {
                    value: required,
                    message: t('ra:requiredFieldError'),
                },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
                <GroupedDropdownList
                    data={data}
                    onChange={onChange}
                    value={value as ExcludeArray<TData>}
                    data-testid={dataTestId}
                    valid={!error}
                    validationMessage={error?.message}
                    disabled={disabled}
                    required={required}
                    groupField={groupField}
                    label={label}
                    dataItemKey={dataItemKey}
                    textField={textField}
                    {...props}
                />
            )}
        />
    );
};

export { GroupedDropdownList, GroupedDropdownListController };
